import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { MagnifyingGlass } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTrackSimilarCTAClicked } from '@/utils/amplitude/analytics/similarItems/trackSimilarCTAClicked.analytics';
import { useSimilarItemsCTAItemFields } from '@/utils/amplitude/analytics/similarItems/useSimilarItemsCTAItemFields';

type SeeSimilarItemsButtonSearchCardProps = {
    isIconCTA: boolean;
    isMobile: boolean;
    itemId: number;
    position: number;
};

export const SeeSimilarItemsButtonSearchCard = ({
    isIconCTA,
    isMobile,
    itemId,
    position,
}: SeeSimilarItemsButtonSearchCardProps) => {
    const navigate = useNavigate();
    const seeSimilarItemsId = `see-similar-items-${itemId}`;
    const trackSimilarCTAClicked = useTrackSimilarCTAClicked();
    const { auctionType, houseId } = useSimilarItemsCTAItemFields(itemId);

    const handleSeeSimilarItemsClick = useCallback(() => {
        trackSimilarCTAClicked({
            ...(auctionType !== undefined && { auctionType }),
            houseId,
            itemId,
            position,
        });
        navigate(`/similar/${itemId}`);
    }, [auctionType, houseId, itemId, navigate, position, trackSimilarCTAClicked]);

    return (
        <span className="absolute bottom-2xs left-2xs z-[8] smMin:bottom-2 smMin:left-2">
            {!isMobile && (
                <Tooltip
                    anchorSelect={`#${seeSimilarItemsId}`}
                    content="See Similar Items"
                    place="top"
                    positionStrategy="fixed"
                />
            )}

            {isIconCTA && (
                <IconButton
                    icon={MagnifyingGlass}
                    id={seeSimilarItemsId}
                    onClick={handleSeeSimilarItemsClick}
                    size="sm"
                    style="neutral"
                    variant="outlined"
                />
            )}

            {!isIconCTA && !isMobile && (
                <Button
                    iconLeft={MagnifyingGlass}
                    id={seeSimilarItemsId}
                    onClick={handleSeeSimilarItemsClick}
                    size="sm"
                    style="neutral"
                    variant="outlined"
                >
                    <FormattedMessage id="searchResults.searchPageItemCard.similarItemsButton" />
                </Button>
            )}

            {!isIconCTA && isMobile && (
                <span className="inline-block rounded-lg border border-solid border-border-tertiary bg-background-primary/70 backdrop-blur-xs">
                    <Button
                        className="h-lg px-xs"
                        iconLeft={MagnifyingGlass}
                        id={seeSimilarItemsId}
                        onClick={handleSeeSimilarItemsClick}
                        size="sm"
                        style="neutral"
                        variant="transparent"
                    >
                        <BodySecondary className="text-text-secondary capitalize">
                            <FormattedMessage id="searchResults.searchPageItemCard.similarItemsButton" />
                        </BodySecondary>
                    </Button>
                </span>
            )}
        </span>
    );
};
